import { useEffect, useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import { isFreelance } from "../../../../utils/constants/accountType";
import { exportTable } from "../../../../utils/exportTable";
import { QuotationStatusBadge, quotationStatusRail } from "../../../../components/Status/QuotationStatus";
import { clientName, personName, formatDateTime, formatMoney } from "../quotationFormat";
import { btn } from "../../../../components/Button/buttonStyles";

const PAGE_SIZE = 25;

// Widths are hints for the auto layout, so a long name never runs into the next
// column. Primary manager is dropped for freelance accounts, which have none.
const buildColumns = (freelance) =>
  [
    { label: "Code", width: "9%" },
    { label: "Name", width: "13%" },
    { label: "Client", width: "13%" },
    { label: "Start At", width: "10%" },
    { label: "Delivery At", width: "10%" },
    { label: "Total", width: "6%", align: "right" },
    { label: "Currency", width: "5%" },
    { label: "Expire At", width: "10%" },
    { label: "Linked Project", width: "11%" },
    { label: "Status", width: "6%" },
    { label: "Closed At", width: "10%" },
    !freelance && { label: "Primary Manager", width: "11%" },
  ].filter(Boolean);

const buildExportColumns = (freelance) =>
  [
    { header: "Code", value: (q) => q.quotation_code || "" },
    { header: "Name", value: (q) => q.quotation_name },
    { header: "Client", value: (q) => clientName(q.client) },
    { header: "Start At", value: (q) => formatDateTime(q.start_at) },
    { header: "Delivery At", value: (q) => formatDateTime(q.deadline_at) },
    { header: "Total", value: (q) => Number(q.total || 0).toFixed(2) },
    { header: "Currency", value: (q) => q.currency || "" },
    { header: "Expire At", value: (q) => formatDateTime(q.expire_at) },
    { header: "Linked Project", value: (q) => q.project?.project_name || "" },
    { header: "Status", value: (q) => q.status },
    { header: "Closed At", value: (q) => formatDateTime(q.closed_at) },
    !freelance && { header: "Primary Manager", value: (q) => personName(q.primaryManager) },
  ].filter(Boolean);

const TEXT_CELL = "px-3 py-2 text-xs align-top text-gray-700 break-words";
const TIGHT_CELL = "px-3 py-2 text-xs align-top text-gray-700 whitespace-nowrap";
const LINK = "text-brand-600 hover:text-brand-800 underline font-medium";
const dash = (value) => value || "—";

const QuotationsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const freelance = isFreelance(user);
  const columns = buildColumns(freelance);

  const [quotations, setQuotations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchQuotations = async () => {
      setLoading(true);
      try {
        const res = await api.get("/quotations", { params: { page, limit: PAGE_SIZE } });
        setQuotations(res.data.data || []);
        setTotal(res.data.meta?.total || 0);
        setError("");
      } catch (err) {
        console.error(err);
        setError("Failed to fetch quotations");
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, [page]);

  const first = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, total);
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Quotations</h1>

        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate("/quotations/create-quotation")}
            className={btn("primary", "sm")}
          >
            New Quotation
          </button>
          <button
            onClick={() => exportTable("quotations", buildExportColumns(freelance), quotations)}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}

      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <p>
              Showing{" "}
              <span className="font-semibold">
                {first}-{last}
              </span>{" "}
              of <span className="font-semibold">{total}</span> {total === 1 ? "item" : "items"}.
            </p>

            {lastPage > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className={btn("secondary", "sm")}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {lastPage}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === lastPage}
                  className={btn("secondary", "sm")}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          <div className="w-full max-w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1400px]">
                <colgroup>
                  {columns.map((column) => (
                    <col key={column.label} style={{ width: column.width }} />
                  ))}
                </colgroup>

                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {columns.map((column) => (
                      <th
                        key={column.label}
                        className={`px-3 py-2 font-semibold align-bottom whitespace-nowrap ${
                          column.align === "right" ? "text-right" : "text-left"
                        }`}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {quotations.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="py-10 text-center text-gray-400 text-xs">
                        No quotations found.
                      </td>
                    </tr>
                  ) : (
                    quotations.map((q, index) => (
                      <tr
                        key={q.id}
                        className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"} border-l-4 ${quotationStatusRail(
                          q.status
                        )} hover:bg-brand-50 transition-colors`}
                      >
                        <td className={TIGHT_CELL}>
                          <Link to={`/quotation/${q.id}`} className={LINK}>
                            {q.quotation_code || `#${q.id}`}
                          </Link>
                        </td>
                        <td className={TEXT_CELL}>{q.quotation_name}</td>
                        <td className={TEXT_CELL}>
                          {q.client?.id ? (
                            <Link to={`/clients/${q.client.id}`} className={LINK}>
                              {dash(clientName(q.client))}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={TIGHT_CELL}>{dash(formatDateTime(q.start_at))}</td>
                        <td className={TIGHT_CELL}>{dash(formatDateTime(q.deadline_at))}</td>
                        <td className={`${TIGHT_CELL} text-right`}>{formatMoney(q.total)}</td>
                        <td className={TIGHT_CELL}>{dash(q.currency)}</td>
                        <td className={TIGHT_CELL}>{dash(formatDateTime(q.expire_at))}</td>
                        <td className={TEXT_CELL}>
                          {q.project ? (
                            <Link to={`/project/${q.project.id}`} className={LINK}>
                              {q.project.project_name}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={TIGHT_CELL}>
                          <QuotationStatusBadge status={q.status} />
                        </td>
                        <td className={TIGHT_CELL}>{dash(formatDateTime(q.closed_at))}</td>
                        {!freelance && <td className={TEXT_CELL}>{dash(personName(q.primaryManager))}</td>}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuotationsPage;
